const FIREBASE_BASE =
  "https://platforms-error-logging-default-rtdb.europe-west1.firebasedatabase.app/ric-web-flasher";

const HANDLERS_FLAG = "__ricWebFlasherErrorHandlersInstalled__";
const SESSION_COOKIE_NAME = "ric-web-flasher-sid";

type VersionInfo = {
  currentVersion?: string | null;
  targetVersion?: string | null;
};

type ErrorContext = {
  type?: "error" | "unhandledrejection" | "console.error" | "manual";
  source?: string | null;
  details?: unknown;
  lineno?: number | null;
  colno?: number | null;
} & VersionInfo;

let handlersInstalled = false;
let cachedSessionId: string | null = null;
let versionInfoForLogging: VersionInfo = {};

function getOrCreateSessionId(): string | null {
  if (cachedSessionId) {
    return cachedSessionId;
  }
  if (typeof document === "undefined") {
    return null;
  }

  const existing = document.cookie
    .split(";")
    .map((entry) => {
      const [name, ...rest] = entry.trim().split("=");
      return [name, rest.join("=")];
    })
    .find(([name]) => name === SESSION_COOKIE_NAME);

  const existingValue = existing?.[1];
  if (existingValue) {
    cachedSessionId = decodeURIComponent(existingValue);
    return cachedSessionId;
  }

  const newId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

  document.cookie = `${SESSION_COOKIE_NAME}=${encodeURIComponent(
    newId
  )}; path=/; SameSite=Lax`;
  cachedSessionId = newId;

  return cachedSessionId;
}

function sanitizeDetails(value: unknown): unknown {
  try {
    return JSON.parse(
      JSON.stringify(value, (_key, val) => {
        if (val instanceof Error) {
          return { name: val.name, message: val.message, stack: val.stack };
        }
        if (typeof val === "bigint") {
          return val.toString();
        }
        return val;
      })
    );
  } catch {
    try {
      return String(value);
    } catch {
      return "Unserializable value";
    }
  }
}

function normalizeError(error: unknown) {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
  }

  if (typeof error === "string") {
    return { message: error };
  }

  if (typeof error === "object" && error !== null) {
    const maybeError = error as { message?: unknown };
    return {
      message:
        typeof maybeError.message === "string"
          ? maybeError.message
          : "Unknown error object",
      details: sanitizeDetails(error),
    };
  }

  return { message: String(error ?? "Unknown error") };
}

function buildPayload(error: unknown, context: ErrorContext) {
  const normalized = normalizeError(error);
  const timestamp = new Date().toISOString();
  const sessionId = getOrCreateSessionId();

  const browserData =
    typeof window !== "undefined"
      ? {
          url: window.location.href,
          userAgent: navigator.userAgent,
        }
      : {};

  return {
    ...normalized,
    ...browserData,
    currentVersion: versionInfoForLogging.currentVersion ?? null,
    targetVersion: versionInfoForLogging.targetVersion ?? null,
    ...context,
    sessionId,
    timestamp,
  };
}

export function logError(error: unknown, context: ErrorContext = { type: "manual" }): void {
  // Avoid breaking the app if logging itself fails.
  try {
    const payload = buildPayload(error, context);
    const body = JSON.stringify(payload);
    const sessionId = payload.sessionId ?? "unknown-session";
    const endpoint = `${FIREBASE_BASE}/${encodeURIComponent(sessionId)}.json`;

    if (typeof fetch !== "undefined") {
      void fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
      }).catch(() => {
        /* ignore logging failures */
      });
    }
  } catch {
    /* ignore logging failures */
  }
}

export function setErrorLogVersions(info: VersionInfo): void {
  const next: VersionInfo = { ...versionInfoForLogging };

  if (info.currentVersion !== undefined) {
    next.currentVersion = info.currentVersion;
  }
  if (info.targetVersion !== undefined) {
    next.targetVersion = info.targetVersion;
  }

  versionInfoForLogging = next;
}

export function installGlobalErrorHandlers(): void {
  if (handlersInstalled || typeof window === "undefined") {
    return;
  }

  const globalWindow = window as unknown as Record<string, unknown>;
  if (Boolean(globalWindow[HANDLERS_FLAG])) {
    handlersInstalled = true;
    return;
  }

  handlersInstalled = true;
  globalWindow[HANDLERS_FLAG] = true;

  window.addEventListener("error", (event) => {
    logError(event.error ?? event.message ?? "Unknown window error", {
      type: "error",
      source: event.filename ?? null,
      lineno: event.lineno ?? null,
      colno: event.colno ?? null,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    logError(event.reason ?? "Unhandled rejection with no reason", {
      type: "unhandledrejection",
    });
  });

  const originalConsoleError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    const firstError = args.find((arg) => arg instanceof Error);
    const message =
      args.length === 1
        ? args[0]
        : firstError ?? args.map((arg) => String(arg)).join(" ");

    logError(message, {
      type: "console.error",
      details: sanitizeDetails(args),
    });
    originalConsoleError(...(args as any[]));
  };
}
